import React, { useEffect, useRef } from 'react';
import envelopeBackNamed from '../assets/animation/envelope_back.png';
import envelopeBackground from '../assets/animation/envelope_background.png';
import envelopeFront from '../assets/animation/envelope_front.png';
import invitationEn from '../assets/animation/invitation_en.png';
import invitationIt from '../assets/animation/invitation_it.png';
import lidClosed from '../assets/animation/lid_closed.png';
import lidOpen from '../assets/animation/lid_open.png';

interface EnvelopeAnimationProps {
  guestName: string;
  language?: 'en' | 'it';
  onReady?: () => void;
  loadingComplete?: boolean;
  onComplete?: () => void;
}

const EnvelopeAnimation: React.FC<EnvelopeAnimationProps> = ({ guestName, language = 'en', onReady, loadingComplete = false, onComplete }) => {
  const invitationImg = language === 'it' ? invitationIt : invitationEn;
  const [imageReady, setImageReady] = React.useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const invitationRef = useRef<HTMLImageElement>(null);
  const lidRef = useRef<HTMLImageElement>(null);
  const envelopeBackRef = useRef<HTMLImageElement>(null);
  const envelopeRef = useRef<HTMLDivElement>(null);

  const hasRunAnimationRef = useRef(false);
  const animationDoneRef = useRef(false);

  // Preload ALL images before signaling ready
  useEffect(() => {
    setImageReady(false);
    const envelope = envelopeRef.current;
    const lid = lidRef.current;
    const invitation = invitationRef.current;
    const canvas = canvasRef.current;

    if (!canvas || !envelope || !lid || !invitation) return;

    const ctx = canvas.getContext('2d');

    // Load the envelope back image for canvas drawing
    const baseImage = new Image();

    // Preload all other images in parallel (invitationImg depends on language)
    const imageSources = [envelopeBackground, envelopeFront, invitationImg, lidClosed, lidOpen];
    let allPreloaded = false;
    let canvasReady = false;

    const checkAllReady = () => {
      if (allPreloaded && canvasReady) {
        setImageReady(true);
        onReady?.();
      }
    };

    // Preload all animation images
    let loadedCount = 0;
    imageSources.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === imageSources.length) {
          allPreloaded = true;
          checkAllReady();
        }
      };
      img.src = src;
    });

    // Draw the canvas (envelope back with guest name)
    const onloadHandler = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);
      ctx.font = '52px Georgia';
      ctx.fillStyle = '#3a2b1a';
      ctx.textAlign = 'center';
      ctx.fillText(`${guestName}`, canvas.width / 2, 400);
      envelopeBackRef.current!.src = canvas.toDataURL('image/png');
      canvasReady = true;
      checkAllReady();
    };

    baseImage.onload = onloadHandler;
    baseImage.src = envelopeBackNamed;

    return () => {
      baseImage.onload = null;
    };
  }, [guestName, onReady, invitationImg]);

  // Start animation once everything is ready and loading is complete
  useEffect(() => {
    if (imageReady && loadingComplete && !hasRunAnimationRef.current) {
      const isPhone = window.innerWidth <= 768;
      const envelope = envelopeRef.current;
      const lid = lidRef.current;
      const invitation = invitationRef.current;

      if (!envelope || !lid || !invitation) return;

      hasRunAnimationRef.current = true;
      const wrappedOnComplete = () => {
        animationDoneRef.current = true;
        onComplete?.();
      };
      if (isPhone) {
        runCommonAnimation(envelope, lid, invitation, 'rotateZ(0deg) scale(1.6)', '28%', '50%', 1500, wrappedOnComplete);
      } else {
        runCommonAnimation(envelope, lid, invitation, 'rotateZ(0deg) scale(1.3)', '15%', '100%', 1500, wrappedOnComplete);
      }
    }
  }, [imageReady, loadingComplete, onComplete]);

  // Update invitation position/scale on resize after animation is done
  useEffect(() => {
    const handleResize = () => {
      if (!animationDoneRef.current) return;
      const invitation = invitationRef.current;
      if (!invitation) return;

      const isPhone = window.innerWidth <= 768;
      if (isPhone) {
        invitation.style.transform = 'rotateZ(0deg) scale(1.6)';
        invitation.style.top = '28%';
        invitation.style.left = '50%';
      } else {
        invitation.style.transform = 'rotateZ(0deg) scale(1.3)';
        invitation.style.top = '15%';
        invitation.style.left = '100%';
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const envelope = envelopeRef.current;
    const container = document.querySelector('.animation-container') as HTMLElement;

    if (!envelope || !container) return;

    const resizeObserver = new ResizeObserver(() => {
      const rect = envelope.getBoundingClientRect();
      const envelopeHeight = rect.height;
      const containerHeight = envelopeHeight * 2.5;

      container.style.height = `${containerHeight}px`;
      envelope.style.bottom = `${envelopeHeight * -0.4}px`;
    });

    resizeObserver.observe(envelope);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="animation-container flex items-start justify-center h-screen">
      <canvas ref={canvasRef} width={1200} height={800} style={{ display: 'none' }} />
      <div className="animation-wrapper">
        <div className="scene">
          <div className="envelope" id="envelope" ref={envelopeRef}>
            <img ref={envelopeBackRef} className="envelope-back" alt="envelope back" style={{ display: imageReady ? 'block' : 'none' }} />
            <div className="front-envelope">
              <img src={envelopeBackground} className="envelope-background" alt="bg" />
              <img src={invitationImg} ref={invitationRef} className="invitation" id="invitation" alt="invitation" />
              <img src={envelopeFront} className="envelope-front" alt="envelope front" />
              <img src={lidClosed} ref={lidRef} className="lid" id="lid" alt="lid" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvelopeAnimation;

function runCommonAnimation(
  envelope: HTMLDivElement,
  lid: HTMLImageElement,
  invitation: HTMLImageElement,
  finalTransform: string,
  finalTop: string,
  finalLeft: string,
  startDelay: number = 2500,
  onComplete?: () => void
) {
  let time = startDelay;
  const preload = new Image();
  preload.src = lidOpen;

  setTimeout(() => {
    envelope!.style.transform = 'rotateY(180deg)';
  }, time);

  time += 3000;
  setTimeout(() => {
    lid.style.transform = 'rotateX(-160deg)';
    setTimeout(() => {
      lid.src = lidOpen;
      lid!.style.zIndex = '2';
    }, 470); 
  }, time);

  time += 1400;
  setTimeout(() => {
    invitation!.style.top = '-55%';
    invitation!.style.transform = 'rotateZ(-90deg) scale(1)';
    invitation!.style.zIndex = '3';
  }, time);

  time += 1300;
  setTimeout(() => {
    invitation!.style.zIndex = '5';
    invitation!.style.transform = 'rotateZ(0deg) scale(1.3)';
    invitation!.style.top = '25%';
    invitation!.style.left = '50%';
  }, time);

  time += 2000;
  setTimeout(() => {
    invitation!.style.transform = finalTransform;
    invitation!.style.top = finalTop;
    invitation!.style.left = finalLeft;
  }, time);

  time += 3000;
  setTimeout(() => {
    const isPhone = window.innerWidth <= 768;
    window.scrollBy({
      top: isPhone ? window.innerHeight / 8 : window.innerHeight / 4,
      left: 0,
      behavior: 'smooth'
    });
    onComplete?.();
  }, time);
}
